/**
 * Tracking de engagement de imágenes Cloudinary (sitewide) para responder
 * "¿QUÉ miran más?" y "¿POR QUÉ?".
 *
 * Dos niveles:
 *   1. Por interacción (open/touch/zoom) → POST inmediato a /api/track-image-view
 *      (fila en `cloudinary_image_views`, log de eventos). + capture PostHog.
 *   2. Acumulador de SESIÓN (este módulo) → al cerrar/ocultar la pestaña manda
 *      UN resumen (`type:"session"`) con, por imagen:
 *        - impressions (entró al viewport)  → exposición
 *        - opens / touches / zooms          → intención
 *        - dwellMs (tiempo con la imagen abierta)
 *        - firstSeenOrder / firstOpenOrder  → posición en la galería
 *      y a nivel sesión: secuencia de apertura, imagen de salida, y la FUENTE
 *      de entrada (referrer + utm) para explicar el "por qué".
 *
 * CTR por imagen = opens / impressions → desbiasa "lo más visto" de la simple
 * exposición (la 1ª foto siempre gana en opens; CTR revela la más atractiva).
 *
 * Los contadores per-tab persistentes (view_index) viven en sessionStorage; el
 * acumulador de sesión vive en memoria del módulo y se vacía al hacer flush.
 */
import {
  getPostHogCorrelationIdsPostHog,
  getClientContextPropertiesPostHog,
  trackCloudinaryImageImpressionPostHog,
  trackCloudinaryImageOpenPostHog,
  trackCloudinaryImageSessionPostHog,
} from "./posthogAnalytics";

const COUNTS_KEY = "cloudinary_image_view_counts_v1";
const TOTAL_KEY = "cloudinary_image_opens_total_v1";
// Evita doble disparo cuando un mismo gesto produce click + touchend.
const RECENT_FIRE_MS = 1200;
const recentFires = new Map<string, number>();

export function isCloudinaryDeliveryUrl(url: string | undefined | null): boolean {
  return !!url && url.includes("res.cloudinary.com");
}

/** Normaliza a public_id (sin versión, sin transforms, sin extensión) para contar. */
export function cloudinaryPublicId(url: string): string {
  const m = url.match(
    /^https:\/\/res\.cloudinary\.com\/[^/]+\/image\/upload\/(.+)$/,
  );
  if (!m) return url;
  const kept: string[] = [];
  for (const seg of m[1].split("/")) {
    if (/^v\d+$/.test(seg)) continue; // versión
    if (seg.includes(",")) continue; // transformación multi-param (w_,c_,…)
    // transformación single-param (overlay/watermark) solo mientras no empezó el public_id
    if (/^(l_|fl_|o_\d|g_|e_|b_|w_\d|h_\d|c_)/.test(seg) && kept.length === 0)
      continue;
    kept.push(seg);
  }
  return kept.join("/").replace(/\.[^./]+$/, "") || url;
}

function readCounts(): Record<string, number> {
  try {
    return JSON.parse(sessionStorage.getItem(COUNTS_KEY) || "{}");
  } catch {
    return {};
  }
}

function bumpCounts(publicId: string): { viewIndex: number; total: number } {
  let viewIndex = 1;
  let total = 1;
  try {
    const counts = readCounts();
    viewIndex = (counts[publicId] ?? 0) + 1;
    counts[publicId] = viewIndex;
    sessionStorage.setItem(COUNTS_KEY, JSON.stringify(counts));

    total = (Number(sessionStorage.getItem(TOTAL_KEY)) || 0) + 1;
    sessionStorage.setItem(TOTAL_KEY, String(total));
  } catch {
    /* sessionStorage no disponible */
  }
  return { viewIndex, total };
}

// ─── Acumulador de sesión (memoria del módulo, por pestaña) ────────────────────

interface PhotoStat {
  publicId: string;
  imageUrl: string;
  altText?: string;
  section?: string;
  impressions: number;     // ÚNICA por sesión (0/1) — denominador correcto del CTR
  impressionHits: number;  // crudo: cada entrada al viewport (intensidad)
  opens: number;
  touches: number;
  zooms: number;
  dwellMs: number;
  firstSeenOrder?: number;
  firstOpenOrder?: number;
}

interface EntryContext {
  referrer?: string;
  source?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  landing?: string;
}

interface ImageSession {
  start: number;
  photos: Map<string, PhotoStat>;
  impressionOrder: number;
  openOrder: number;
  sequence: string[]; // publicIds en orden de apertura
  exitPublicId?: string;
  currentOpen?: { publicId: string; since: number };
  entry: EntryContext;
  flushed: boolean;
}

let session: ImageSession | null = null;

function captureEntryContext(): EntryContext {
  const ctx: EntryContext = {};
  try {
    ctx.referrer = document.referrer || undefined;
    const params = new URLSearchParams(window.location.search);
    ctx.utmSource = params.get("utm_source") || undefined;
    ctx.utmMedium = params.get("utm_medium") || undefined;
    ctx.utmCampaign = params.get("utm_campaign") || undefined;
    ctx.utmContent = params.get("utm_content") || undefined;
    ctx.landing =
      sessionStorage.getItem("entry_page_url") ||
      window.location.href.slice(0, 500);
    // "source" legible: utm_source > host del referrer > "directo"
    if (ctx.utmSource) ctx.source = ctx.utmSource;
    else if (ctx.referrer) {
      try {
        ctx.source = new URL(ctx.referrer).hostname.replace(/^www\./, "");
      } catch {
        /* */
      }
    } else ctx.source = "directo";
  } catch {
    /* */
  }
  return ctx;
}

function getSession(): ImageSession {
  if (!session || session.flushed) {
    session = {
      start: Date.now(),
      photos: new Map(),
      impressionOrder: 0,
      openOrder: 0,
      sequence: [],
      entry: captureEntryContext(),
      flushed: false,
    };
  }
  return session;
}

function statFor(
  s: ImageSession,
  publicId: string,
  imageUrl: string,
  meta: { altText?: string; section?: string },
): PhotoStat {
  let stat = s.photos.get(publicId);
  if (!stat) {
    stat = {
      publicId,
      imageUrl,
      altText: meta.altText,
      section: meta.section,
      impressions: 0,
      impressionHits: 0,
      opens: 0,
      touches: 0,
      zooms: 0,
      dwellMs: 0,
    };
    s.photos.set(publicId, stat);
  }
  // Completar metadata si llegó después
  if (!stat.altText && meta.altText) stat.altText = meta.altText;
  if (!stat.section && meta.section) stat.section = meta.section;
  return stat;
}

/** Cierra el dwell de la imagen actualmente "abierta" sumando el tiempo transcurrido. */
function closeCurrentDwell(s: ImageSession, now: number): void {
  if (!s.currentOpen) return;
  const stat = s.photos.get(s.currentOpen.publicId);
  if (stat) stat.dwellMs += Math.max(0, now - s.currentOpen.since);
  s.currentOpen = undefined;
}

export interface ImpressionMeta {
  altText?: string;
  section?: string;
}

/** Registra que el thumbnail de una imagen entró al viewport (exposición). */
export function recordImageImpression(
  imageUrl: string,
  meta: ImpressionMeta = {},
): void {
  if (typeof window === "undefined" || !isCloudinaryDeliveryUrl(imageUrl)) return;
  const s = getSession();
  const publicId = cloudinaryPublicId(imageUrl);
  const stat = statFor(s, publicId, imageUrl, meta);
  stat.impressionHits += 1;
  if (stat.firstSeenOrder == null) {
    stat.impressions = 1; // única por sesión
    stat.firstSeenOrder = ++s.impressionOrder;
    try {
      trackCloudinaryImageImpressionPostHog({
        publicId,
        section: stat.section,
        pagePath: window.location.pathname,
        firstSeenOrder: stat.firstSeenOrder,
      });
    } catch {
      /* */
    }
  }
}

export interface TrackImageOptions {
  altText?: string;
  section?: string;
  interactionType?: "open" | "touch" | "zoom" | "swipe";
  template?: string;
  /** miniatura ya optimizada/sin watermark para mostrar en Slack */
  thumbUrl?: string;
}

/**
 * Registra la apertura/tap/zoom de una imagen Cloudinary. No-op si no es Cloudinary.
 * Alimenta el acumulador de sesión, manda el evento inmediato y captura en PostHog.
 */
export function trackCloudinaryImageView(
  imageUrl: string,
  opts: TrackImageOptions = {},
): void {
  if (typeof window === "undefined") return;
  if (!isCloudinaryDeliveryUrl(imageUrl)) return;

  const publicId = cloudinaryPublicId(imageUrl);
  const interaction = opts.interactionType ?? "open";

  // Dedupe del gesto (click+touch) — salvo zoom, que es intencional y se cuenta.
  if (interaction !== "zoom") {
    const now = Date.now();
    const last = recentFires.get(publicId);
    if (last != null && now - last < RECENT_FIRE_MS) return;
    recentFires.set(publicId, now);
    if (recentFires.size > 500) {
      for (const [k, ts] of recentFires) {
        if (now - ts > RECENT_FIRE_MS) recentFires.delete(k);
      }
    }
  }

  const { viewIndex, total } = bumpCounts(publicId);

  // ── Acumulador de sesión ──────────────────────────────────────────────────
  const s = getSession();
  const now = Date.now();
  const stat = statFor(s, publicId, imageUrl, {
    altText: opts.altText,
    section: opts.section,
  });
  if (interaction === "touch") {
    stat.touches += 1;
  } else if (interaction === "zoom") {
    stat.zooms += 1;
  } else {
    stat.opens += 1;
  }
  if (interaction === "open" || interaction === "zoom") {
    // dwell: cierra el de la imagen previa y abre el de esta
    closeCurrentDwell(s, now);
    s.currentOpen = { publicId, since: now };
    if (stat.firstOpenOrder == null) stat.firstOpenOrder = ++s.openOrder;
    if (s.sequence[s.sequence.length - 1] !== publicId) s.sequence.push(publicId);
    s.exitPublicId = publicId; // la última imagen abierta = candidata a salida
  }

  // ── Correlación + contexto ──────────────────────────────────────────────────
  let ph: ReturnType<typeof getPostHogCorrelationIdsPostHog> = {
    distinct_id: null,
    session_id: null,
    session_replay_url: null,
  };
  try {
    ph = getPostHogCorrelationIdsPostHog();
  } catch {
    /* */
  }
  const ctx = getClientContextPropertiesPostHog();

  let language = "";
  try {
    language =
      document.documentElement.lang || (navigator.language ?? "").slice(0, 16);
  } catch {
    /* */
  }

  // ── PostHog (evento por interacción) ────────────────────────────────────────
  try {
    trackCloudinaryImageOpenPostHog({
      publicId,
      section: opts.section,
      altText: opts.altText,
      interactionType: interaction,
      viewIndex,
      sessionImageOpens: total,
      openOrder: stat.firstOpenOrder,
      firstSeenOrder: stat.firstSeenOrder,
      pagePath: window.location.pathname,
      template: opts.template,
    });
  } catch {
    /* */
  }

  // ── Beacon inmediato (log de eventos en Postgres) ───────────────────────────
  const payload = {
    imageUrl,
    thumbUrl: opts.thumbUrl,
    publicId,
    altText: opts.altText,
    section: opts.section,
    interactionType: interaction,
    viewIndex,
    sessionImageOpens: total,
    firstSeenOrder: stat.firstSeenOrder,
    pagePath: window.location.pathname,
    pageUrl: window.location.href.slice(0, 1000),
    template: opts.template,
    language,
    phDistinctId: ph.distinct_id ?? undefined,
    phSessionId: ph.session_id ?? undefined,
    phReplayUrl: ph.session_replay_url ?? undefined,
    sessionId: ph.session_id ?? undefined,
    viewportW: ctx.viewport_width as number | undefined,
    viewportH: ctx.viewport_height as number | undefined,
    dpr: ctx.device_pixel_ratio as number | undefined,
    browserTz: ctx.browser_timezone as string | undefined,
  };
  postBeacon(payload);
}

/** Atajo semántico para zoom (pinch / doble tap / rueda+ctrl). */
export function recordImageZoom(imageUrl: string, opts: TrackImageOptions = {}): void {
  trackCloudinaryImageView(imageUrl, { ...opts, interactionType: "zoom" });
}

/**
 * Vacía el acumulador de sesión: manda UN resumen con métricas por imagen +
 * contexto de la sesión, y captura el evento de sesión en PostHog. Idempotente.
 */
export function flushImageSession(): void {
  if (typeof window === "undefined") return;
  const s = session;
  if (!s || s.flushed) return;
  closeCurrentDwell(s, Date.now());

  const photos = [...s.photos.values()].filter(
    (p) => p.impressions > 0 || p.opens > 0 || p.zooms > 0 || p.touches > 0,
  );
  if (photos.length === 0) {
    s.flushed = true;
    return;
  }

  const totalImpressions = photos.reduce((a, p) => a + p.impressions, 0);
  const totalImpressionHits = photos.reduce((a, p) => a + p.impressionHits, 0);
  const totalOpens = photos.reduce((a, p) => a + p.opens, 0);
  const totalZooms = photos.reduce((a, p) => a + p.zooms, 0);
  const totalDwellMs = photos.reduce((a, p) => a + p.dwellMs, 0);
  const durationMs = Date.now() - s.start;

  // "Más visto" por dwell; "más atractivo" por CTR (opens/impressions).
  const byDwell = [...photos].sort((a, b) => b.dwellMs - a.dwellMs)[0];
  const byCtr = [...photos]
    .filter((p) => p.impressions > 0)
    .sort(
      (a, b) => b.opens / b.impressions - a.opens / a.impressions,
    )[0];

  let ph: ReturnType<typeof getPostHogCorrelationIdsPostHog> = {
    distinct_id: null,
    session_id: null,
    session_replay_url: null,
  };
  try {
    ph = getPostHogCorrelationIdsPostHog();
  } catch {
    /* */
  }
  const ctx = getClientContextPropertiesPostHog();
  let language = "";
  try {
    language =
      document.documentElement.lang || (navigator.language ?? "").slice(0, 16);
  } catch {
    /* */
  }

  const payload = {
    type: "session",
    photos: photos.map((p) => ({
      publicId: p.publicId,
      imageUrl: p.imageUrl,
      altText: p.altText,
      section: p.section,
      impressions: p.impressions,
      impressionHits: p.impressionHits,
      opens: p.opens,
      touches: p.touches,
      zooms: p.zooms,
      dwellMs: p.dwellMs,
      firstSeenOrder: p.firstSeenOrder,
      firstOpenOrder: p.firstOpenOrder,
      ctr: p.impressions > 0 ? Math.round((p.opens / p.impressions) * 100) / 100 : null,
    })),
    photoCount: photos.length,
    totalImpressions,
    totalImpressionHits,
    totalOpens,
    totalZooms,
    totalDwellMs,
    durationMs,
    sequence: s.sequence.join(","),
    exitPublicId: s.exitPublicId,
    topPublicId: byDwell?.publicId,
    topByCtrPublicId: byCtr?.publicId,
    // "Por qué": fuente de entrada
    source: s.entry.source,
    referrer: s.entry.referrer,
    utmSource: s.entry.utmSource,
    utmMedium: s.entry.utmMedium,
    utmCampaign: s.entry.utmCampaign,
    utmContent: s.entry.utmContent,
    landing: s.entry.landing,
    // Contexto
    pagePath: window.location.pathname,
    template: document.documentElement.dataset.template || undefined,
    language,
    phDistinctId: ph.distinct_id ?? undefined,
    phSessionId: ph.session_id ?? undefined,
    phReplayUrl: ph.session_replay_url ?? undefined,
    sessionId: ph.session_id ?? undefined,
    viewportW: ctx.viewport_width as number | undefined,
    viewportH: ctx.viewport_height as number | undefined,
    dpr: ctx.device_pixel_ratio as number | undefined,
  };

  try {
    trackCloudinaryImageSessionPostHog({
      photoCount: photos.length,
      totalImpressions,
      totalOpens,
      totalZooms,
      totalDwellMs,
      durationMs,
      topPublicId: byDwell?.publicId,
      topByCtrPublicId: byCtr?.publicId,
      exitPublicId: s.exitPublicId,
      sequence: s.sequence.join(","),
      source: s.entry.source,
      referrer: s.entry.referrer,
      pagePath: window.location.pathname,
      template: payload.template,
    });
  } catch {
    /* */
  }

  s.flushed = true;
  postBeacon(payload);
}

// ─── Envío ─────────────────────────────────────────────────────────────────────
function postBeacon(payload: Record<string, unknown>): void {
  const url = "/api/track-image-view";
  const bodyStr = JSON.stringify(payload);
  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([bodyStr], { type: "application/json" });
      if (navigator.sendBeacon(url, blob)) return;
    }
  } catch {
    /* cae a fetch */
  }
  void fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: bodyStr,
    keepalive: true,
  }).catch(() => {});
}
